/** @jsxImportSource react */

import React, { useMemo } from "react";
import type { MesaWithComparison } from "../lib/types";
import {
  getDistritoName,
  resolveDistritoId,
} from "../../shared/lib/district-mappings";

interface BarrasChartProps {
  data: MesaWithComparison[];
  statMode: "median" | "average";
  barrasMode: "absoluto" | "relativo";
}

export function BarrasChart({ data, statMode, barrasMode }: BarrasChartProps) {
  // Agrupar por provincia
  const grouped = useMemo(() => {
    const map = new Map<string, { lla: number[]; fp: number[] }>();
    data.forEach((mesa: MesaWithComparison) => {
      const provId = resolveDistritoId(mesa.data.distrito) || "??";
      if (!map.has(provId)) map.set(provId, { lla: [], fp: [] });
      if (mesa.partyComparison) {
        map.get(provId)!.lla.push(mesa.partyComparison.lla2025vsJxC_LLA2023);
        map.get(provId)!.fp.push(mesa.partyComparison.fuerzaPatria2025vs2023);
      }
    });
    return map;
  }, [data]);

  // Calcular mediana/promedio por provincia y fuerza
  const calc = (arr: number[]): number => {
    if (!arr || arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    if (statMode === "median") {
      const mid = Math.floor(sorted.length / 2);
      if (sorted.length % 2 !== 0) {
        return sorted[mid] ?? 0;
      } else {
        return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
      }
    } else {
      return arr.reduce((a, b) => a + b, 0) / arr.length;
    }
  };

  // Preparar datos para renderizado
  const provincias = useMemo(
    () => Array.from(grouped.keys()).sort(),
    [grouped],
  );
  const chartData = useMemo(
    () =>
      provincias.map((provId: string) => {
        const llaArr = grouped.get(provId)?.lla ?? [];
        const fpArr = grouped.get(provId)?.fp ?? [];
        return {
          provId,
          provName: getDistritoName(provId) || provId,
          lla: calc(llaArr),
          fp: calc(fpArr),
        };
      }),
    [provincias, grouped, statMode],
  );

  // Encontrar máximos para escalas
  const allVals = chartData.flatMap((d: { lla: number; fp: number }) => [
    d.lla,
    d.fp,
  ]);
  const maxAbs = Math.max(
    1,
    ...allVals.map((v: number) => (typeof v === "number" ? Math.abs(v) : 0)),
  );

  // Barras horizontales agrupadas: cada provincia tiene dos barras (LLA y FP)
  return (
    <div className="flex-1 w-full h-full min-h-0 min-w-0 flex flex-col overflow-y-auto px-4 py-4">
      {chartData.map((prov) => (
        <div key={prov.provId} className="flex items-center gap-3 mb-1">
          {/* Etiqueta provincia */}
          <div
            className="text-xs text-slate-700 font-semibold text-right w-32 shrink-0"
            title={prov.provName}
          >
            {prov.provName}
          </div>
          {/* Barras agrupadas */}
          <div className="flex flex-col gap-0 flex-1">
            {/* LLA barra */}
            <BarHorizontal
              value={prov.lla}
              maxAbs={maxAbs}
              color="purple"
              barrasMode={barrasMode}
              label="LLA"
            />
            {/* FP barra */}
            <BarHorizontal
              value={prov.fp}
              maxAbs={maxAbs}
              color="blue"
              barrasMode={barrasMode}
              label="FP"
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// Barra horizontal agrupada
function BarHorizontal({
  value,
  maxAbs,
  color,
  barrasMode,
  label,
}: {
  value: number;
  maxAbs: number;
  color: "purple" | "blue";
  barrasMode: "absoluto" | "relativo";
  label: string;
}) {
  // Escala lineal simple
  const pct = Math.abs(value) / maxAbs;
  const maxWidth = 400; // ancho máximo de la barra
  const width = Math.max(4, pct * maxWidth);
  const isNeg = value < 0;
  const baseColor = color === "purple" ? "bg-purple-500" : "bg-blue-500";
  const bg = baseColor + (isNeg ? " opacity-40" : " opacity-80");

  // En modo relativo, eje 0 al centro
  if (barrasMode === "relativo") {
    return (
      <div className="relative flex items-center h-5 w-full">
        {/* Eje 0 */}
        <div className="absolute left-1/2 top-1/2 -translate-y-1/2 w-0.5 h-full bg-slate-300 z-0"></div>
        {/* Barra */}
        <div
          className={`absolute top-0 h-full rounded ${bg}`}
          style={{
            width: `${width}px`,
            left: isNeg ? `calc(50% - ${width}px)` : "50%",
          }}
          title={`${label}: ${value >= 0 ? "+" : ""}${value.toFixed(1)}`}
        ></div>
        {/* Etiqueta */}
        <span className="absolute left-0 text-[9px] font-semibold uppercase text-slate-600 z-10">
          {label}
        </span>
        {/* Valor */}
        <span
          className="absolute text-[9px] text-slate-700 font-bold z-10"
          style={{
            left: isNeg
              ? `calc(50% - ${width}px - 30px)`
              : `calc(50% + ${width}px + 4px)`,
          }}
        >
          {value >= 0 ? `+${value.toFixed(1)}` : value.toFixed(1)}
        </span>
      </div>
    );
  }

  // Modo absoluto: eje 0 a la izquierda
  return (
    <div className="relative flex items-center h-5 w-full">
      {/* Eje 0 */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-full bg-slate-300 z-0"></div>
      {/* Barra */}
      <div
        className={`absolute top-0 h-full rounded ${bg}`}
        style={{
          width: `${width}px`,
          left: isNeg ? `${Math.max(0, maxWidth - width)}px` : "0",
        }}
        title={`${label}: ${value >= 0 ? "+" : ""}${value.toFixed(1)}`}
      ></div>
      {/* Etiqueta */}
      <span className="absolute left-0 text-[9px] font-semibold uppercase text-slate-600 z-10 -translate-x-full pr-2">
        {label}
      </span>
      {/* Valor */}
      <span
        className="absolute text-[9px] text-slate-700 font-bold z-10"
        style={{
          left: isNeg
            ? `${Math.max(0, maxWidth - width) - 30}px`
            : `${width + 4}px`,
        }}
      >
        {value >= 0 ? `+${value.toFixed(1)}` : value.toFixed(1)}
      </span>
    </div>
  );
}
